import React, { useEffect, useState } from "react";
import {
  View,
  Text,
  TextInput,
  Button,
  Alert,
  StyleSheet,
  Platform,
} from "react-native";
import { NativeStackScreenProps } from "@react-navigation/native-stack";
import { RootStackParamList } from "../routes";
import { WorthApi } from "../api/WorthApi";

type Props = NativeStackScreenProps<RootStackParamList, "VestingEdit">;

export default function VestingEdit({ route, navigation }: Props) {
  const { id } = route.params;
  const [loading, setLoading] = useState(true);
  const [grantDate, setGrantDate] = useState("");
  const [shares, setShares] = useState("");
  const [symbol, setSymbol] = useState("");
  const [deferralYears, setDeferralYears] = useState("3");
  const [vestedOverYears, setVestedOverYears] = useState("3");
  const [notes, setNotes] = useState("");

  useEffect(() => {
    let mounted = true;
    WorthApi.get(`/vesting/grants/${id}`)
      .then((res) => {
        if (!mounted) return;
        setGrantDate(res.grantDate ? res.grantDate.slice(0, 10) : "");
        setShares(String(res.shares));
        setSymbol(res.symbol || "");
        setDeferralYears(String(res.deferralYears || 3));
        setVestedOverYears(
          String(res.vestedOverYears ?? res.deferralYears ?? 3),
        );
        setNotes(res.notes || "");
      })
      .catch((e) => {
        console.warn("Failed to load grant", e);
        Alert.alert("Error", "Failed to load grant");
        navigation.goBack();
      })
      .finally(() => setLoading(false));
    return () => {
      mounted = false;
    };
  }, [id]);

  function validateAndPatch() {
    const sharesNum = Number(shares);
    if (!Number.isFinite(sharesNum) || sharesNum <= 0) {
      Alert.alert("Validation", "Shares must be a number greater than 0");
      return;
    }
    if (!symbol || symbol.trim() === "") {
      Alert.alert("Validation", "Symbol is required");
      return;
    }
    WorthApi.patch(`/vesting/grants/${id}`, {
      grantDate,
      shares: sharesNum,
      symbol,
      deferralYears: Number(deferralYears) || 3,
      vestedOverYears: Number(vestedOverYears) || Number(deferralYears) || 3,
      notes,
    })
      .then(() => {
        navigation.goBack();
      })
      .catch((e) => {
        console.warn("Failed to update grant", e);
        Alert.alert("Error", "Failed to update grant");
      });
  }

  if (loading) return <View style={{ flex: 1, backgroundColor: "#201D2B" }} />;

  return (
    <View
      style={[
        styles.container,
        Platform.OS === "web" ? { minHeight: "100vh" } : {},
      ]}
    >
      <Text style={styles.title}>Edit Vesting Grant</Text>

      <Text style={styles.label}>Grant date (YYYY-MM-DD)</Text>
      <TextInput
        style={styles.input}
        value={grantDate}
        onChangeText={setGrantDate}
      />

      <Text style={styles.label}>Symbol</Text>
      <TextInput style={styles.input} value={symbol} onChangeText={setSymbol} />

      <Text style={styles.label}>Shares</Text>
      <TextInput
        style={styles.input}
        value={shares}
        onChangeText={setShares}
        keyboardType="numeric"
      />

      <Text style={styles.label}>Deferral years</Text>
      <TextInput
        style={styles.input}
        value={deferralYears}
        onChangeText={setDeferralYears}
        keyboardType="numeric"
      />

      <Text style={styles.label}>Vested over (years)</Text>
      <TextInput
        style={styles.input}
        value={vestedOverYears}
        onChangeText={setVestedOverYears}
        keyboardType="numeric"
      />

      <Text style={styles.label}>Notes</Text>
      <TextInput style={styles.input} value={notes} onChangeText={setNotes} />

      <Button title="Save" onPress={validateAndPatch} />
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, padding: 12, backgroundColor: "#201D2B" },
  title: { fontSize: 20, color: "white", marginBottom: 12 },
  input: {
    borderWidth: 1,
    borderColor: "#444",
    padding: 8,
    color: "white",
    marginBottom: 8,
  },
  label: { color: "white", marginBottom: 4 },
});
